import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { HttpStatusCode, isAxiosError } from "axios";
import z, { ZodError } from "zod";

import { IS_PRODUCTION } from "@/config/env";
import { ExpectedStripeError } from "../stripe";

const NO_RETRY_STATUSES: readonly number[] = [
  HttpStatusCode.Unauthorized,
  HttpStatusCode.Forbidden,
  HttpStatusCode.NotFound,
];

const UNREPORTED_QUERY_STATUSES: readonly number[] = [
  HttpStatusCode.Unauthorized,
];

const UNREPORTED_MUTATION_STATUSES: readonly number[] = [
  HttpStatusCode.Unauthorized,
  HttpStatusCode.UnprocessableEntity,
];

const consoleLogZodError = (error: ZodError) => {
  console.error(z.prettifyError(error));
  console.table(
    error._zod.def.map(({ path, input, ...rest }) => ({
      path: path.slice(2).join("."),
      input: input,
      ...rest,
    })),
  );
};

const hasStatus = (error: Error, statuses: readonly number[]) =>
  isAxiosError(error) &&
  error.response?.status !== undefined &&
  statuses.includes(error.response.status);

const reportToSentry = (
  error: Error,
  unreportedStatuses: readonly number[],
) => {
  if (!IS_PRODUCTION) {
    if (error instanceof ZodError) {
      consoleLogZodError(error);
    }
    return;
  }

  if (
    error instanceof ExpectedStripeError ||
    hasStatus(error, unreportedStatuses)
  ) {
    return;
  }

  if (error instanceof ZodError) {
    const prettyError = z.prettifyError(error);

    Sentry.captureException(error, {
      extra: { zod: prettyError },
      fingerprint: ["{{ default }}", prettyError],
    });
    return;
  }

  Sentry.captureException(error);
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      reportToSentry(error, UNREPORTED_QUERY_STATUSES);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      reportToSentry(error, UNREPORTED_MUTATION_STATUSES);
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        return error instanceof ZodError || hasStatus(error, NO_RETRY_STATUSES)
          ? false
          : failureCount < 3;
      },
    },
  },
});
